import React, { useState } from 'react';
import styled from 'styled-components';

import CardActionModal from './CardActionModal';
import CardActionColumn from './CardActionColumn';

import Headline3 from '@joinforma/ui/components/Headline3';
import Body2 from '@joinforma/ui/components/Body2';
import Title2 from '@joinforma/ui/components/Title2';
import Button from '@joinforma/ui/components/Button';
import { ButtonVariant, ButtonSize } from '@joinforma/ui/constants/properties';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import { FormaCard, FormaCardMember } from './types';
import ReissueCard from './ReissueCard';
import DebitCard from 'components/common/DebitCard';
import PendingCard from 'components/common/PendingCard';

import { CardStatus } from 'constants/unit';
import { PretaxAccountV2Type } from 'constants/accounts';

enum MODAL_STATES {
  ACTIVATE_CARD = 'activate',
  RESET_PIN = 'reset pin',
  REPLACE_VIRTUAL_CARD = 'replace virtual',
  REPLACE_PHYSICAL_CARD = 'replace physical',
  REPLACE_CARD_SHIPPING_ADDRESS = 'shipping address',
  // default state
  NONE = 'none',
}

interface CardActionButtonProps {
  highlighted?: boolean;
}

interface Props {
  inactive_physical_cards?: Array<FormaCard>;
  active_or_pending_physical_cards?: Array<FormaCard>;
  availablePretaxAccountTypes: Array<string>;
  preferred_address?: FormaCardMember['billing_address'];
  forma_card_member: FormaCardMember;
  employee_first_name: string;
  employee_last_name: string;
  employee_id: FormaCardMember['employee_id'];
  refetchCards: () => void;
}

interface ShippingAddressProps {
  shipping_address: FormaCard['cardHolderDetails']['shippingAddress'];
  billing_address: FormaCard['cardHolderDetails']['billingAddress'];
}

const Container = styled.div``;

const CardInformationContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 15px 0 64px;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    flex-direction: column;
  }
`;

const ShippingAddressContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 0 27px;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 277px;
    margin: 25px 0 0;
  }
`;

const CardTitle = styled(Headline3)`
  margin-bottom: 25px;
`;

const AddressInformation = styled(Title2)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Description = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LeftColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CardActionButton = styled(Button).attrs(
  (props: CardActionButtonProps) => ({
    variant: props.highlighted
      ? ButtonVariant.PRIMARY
      : ButtonVariant.SECONDARY,
    size: ButtonSize.SMALL,
  })
)<CardActionButtonProps>`
  width: 277px;
  height: 48px;
  margin: 12px 0;
  font-size: 15px;
  line-height: 15px;
`;

const ShippingAddress = ({
  shipping_address,
  billing_address,
}: ShippingAddressProps) => {
  const address = shipping_address || billing_address;

  return (
    <>
      <AddressInformation>{address.street}</AddressInformation>
      <AddressInformation>{address.street2}</AddressInformation>
      <AddressInformation>{`${address.city}, ${address.state} ${address.postalCode}`}</AddressInformation>
    </>
  );
};

const CardInformation = ({
  forma_card_member,
  employee_first_name,
  employee_last_name,
  inactive_physical_cards,
  active_or_pending_physical_cards,
  availablePretaxAccountTypes,
  preferred_address,
  employee_id,
  refetchCards,
}: Props) => {
  const [active_modal, setActiveModal] = useState(MODAL_STATES.NONE);
  const handleCloseModal = () => setActiveModal(MODAL_STATES.NONE);

  const reissue_physical_card =
    !active_or_pending_physical_cards?.length &&
    !inactive_physical_cards?.length;

  const DisplayDescription = ({
    cardHolderFirstName,
    cardHolderLastName,
  }: {
    cardHolderFirstName?: string;
    cardHolderLastName?: string;
  }) => {
    const isDependent =
      cardHolderFirstName !== employee_first_name &&
      cardHolderLastName !== employee_last_name;

    const dependentAccountsList = availablePretaxAccountTypes
      .filter(
        (type) =>
          type !== PretaxAccountV2Type.TRANSIT.toUpperCase() &&
          type !== PretaxAccountV2Type.PARKING.toUpperCase()
      )
      .map((type, index) =>
        availablePretaxAccountTypes.length === 1
          ? `${type}`
          : index < availablePretaxAccountTypes.length - 1
          ? `${type}, `
          : ` and ${type}`
      )
      .join('');
    const nonDependentAccountsList = availablePretaxAccountTypes
      .map((type, index) =>
        availablePretaxAccountTypes.length === 1
          ? `${type}`
          : index < availablePretaxAccountTypes.length - 1
          ? `${type}, `
          : ` and ${type}`
      )
      .join('');

    const eligibleAccountsList = isDependent
      ? dependentAccountsList
      : nonDependentAccountsList;

    const hasParkingorTransit =
      eligibleAccountsList.includes(
        PretaxAccountV2Type.TRANSIT.toUpperCase()
      ) ||
      eligibleAccountsList.includes(PretaxAccountV2Type.PARKING.toUpperCase());

    const newAccountsListString = hasParkingorTransit
      ? eligibleAccountsList.concat(' benefits')
      : eligibleAccountsList.concat('');

    return (
      <Description>
        {cardHolderFirstName &&
          cardHolderLastName &&
          `Use this card for your ${newAccountsListString} at any merchant in-store or online.`}
      </Description>
    );
  };

  return (
    <Container>
      {active_or_pending_physical_cards?.map(
        ({ cardType, cardStatus, cardId, cardHolderDetails, cardLastFour }) => (
          <Container key={cardId}>
            {cardHolderDetails?.fullName && cardLastFour ? (
              <CardTitle>{`${cardHolderDetails?.fullName.firstName}'s physical debit card ending in (*${cardLastFour})`}</CardTitle>
            ) : (
              <CardTitle>Debit Card</CardTitle>
            )}

            {DisplayDescription({
              cardHolderFirstName: cardHolderDetails?.fullName.firstName,
              cardHolderLastName: cardHolderDetails?.fullName.lastName,
            })}

            <CardInformationContainer>
              <LeftColumn>
                {cardStatus === CardStatus.PENDING ? (
                  <PendingCard />
                ) : (
                  <DebitCard
                    card_type={cardType}
                    is_locked={cardStatus === CardStatus.FROZEN}
                  />
                )}
              </LeftColumn>
              {cardStatus !== CardStatus.PENDING && (
                <CardActionColumn
                  cardType={cardType}
                  cardId={cardId}
                  is_frozen={cardStatus === CardStatus.FROZEN}
                  shippingAddress={
                    cardHolderDetails?.shippingAddress ||
                    cardHolderDetails?.billingAddress
                  }
                  preferedAddress={preferred_address}
                  refetchCards={refetchCards}
                />
              )}
            </CardInformationContainer>
          </Container>
        )
      )}

      {inactive_physical_cards?.map(
        ({ cardType, cardId, cardHolderDetails, cardLastFour }) => (
          <Container key={cardId}>
            {cardHolderDetails?.fullName && cardLastFour ? (
              <CardTitle>{`${cardHolderDetails?.fullName?.firstName}'s physical debit card ending in ${cardLastFour}`}</CardTitle>
            ) : (
              <CardTitle>Debit Card</CardTitle>
            )}
            {DisplayDescription({
              cardHolderFirstName: cardHolderDetails?.fullName.firstName,
              cardHolderLastName: cardHolderDetails?.fullName.lastName,
            })}
            <CardInformationContainer key={cardId}>
              <LeftColumn>
                <DebitCard card_type={cardType} is_locked={false} />
              </LeftColumn>
              <ShippingAddressContainer>
                <Description>
                  Your new card has been mailed to your address:
                </Description>
                {cardHolderDetails && (
                  <ShippingAddress
                    shipping_address={cardHolderDetails?.shippingAddress}
                    billing_address={cardHolderDetails.billingAddress}
                  />
                )}

                <CardActionButton
                  highlighted
                  onClick={() => setActiveModal(MODAL_STATES.ACTIVATE_CARD)}
                >
                  Activate card
                </CardActionButton>
                <CardActionModal
                  is_open={active_modal === MODAL_STATES.ACTIVATE_CARD}
                  title={'Activate physical debit card'}
                  card_action_description="to activate your card. You'll set your PIN during the activation call as well."
                  onCancel={handleCloseModal}
                />
              </ShippingAddressContainer>
            </CardInformationContainer>
          </Container>
        )
      )}
      {reissue_physical_card && (
        <ReissueCard
          cardType="physical"
          description="Because you closed your card, you'll need to re-issue a new card to receive it in the mail."
          forma_card_member={forma_card_member}
          employee_id={employee_id}
          refetchCards={refetchCards}
        />
      )}
    </Container>
  );
};

export default CardInformation;
